/**
 * WI 34417 (decision B1): the bank and pay-card field rules, for use by the employee Banking step (#29916), HR's
 * override (#34419) and the export pre-check. The C# twin is the packet API's `BankValidators` (UPS.EmployeePacket.Services/Validation);
 * both are tested against `bank-validation-vectors.json` next to this file and must return the same messages. Each
 * function returns null when the value is valid, otherwise a message that never contains the value (bank data).
 * Values are checked as given (no trimming), so what is checked is what is stored and sent to Viewpoint.
 */

export const BANK_ACCOUNT_MIN_LENGTH = 4;
/** Viewpoint's btPREHi trigger rejects longer accounts (incident 13637). */
export const BANK_ACCOUNT_MAX_LENGTH = 17;
export const BANK_TEXT_MAX_LENGTH = 100;
/** Viewpoint udComdataPayCard. */
export const COMDATA_PAY_CARD_MAX_LENGTH = 16;
export const PER_DIEM_PAY_CARD_MAX_LENGTH = 20;

/** 9 digits whose ABA checksum 3(d1+d4+d7) + 7(d2+d5+d8) + (d3+d6+d9) is a multiple of 10; never all zeros. */
export function routingNumberError(
  value: string | null | undefined
): string | null {
  if (!value) {
    return 'Bank routing number is required.';
  }
  if (!/^[0-9]{9}$/.test(value)) {
    return 'Bank routing number must be exactly 9 digits.';
  }
  const d = Array.from(value, Number);
  const checksum =
    3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + d[2] + d[5] + d[8];
  return checksum % 10 !== 0 || value === '000000000'
    ? 'Bank routing number is not a valid ABA routing number (check digit).'
    : null;
}

/** Digits only, 4 to 17 of them. */
export function accountNumberError(
  value: string | null | undefined
): string | null {
  if (!value) {
    return 'Bank account number is required.';
  }
  if (!/^[0-9]+$/.test(value)) {
    return 'Bank account number must contain digits only.';
  }
  return value.length < BANK_ACCOUNT_MIN_LENGTH ||
    value.length > BANK_ACCOUNT_MAX_LENGTH
    ? `Bank account number must be ${BANK_ACCOUNT_MIN_LENGTH} to ${BANK_ACCOUNT_MAX_LENGTH} digits (Viewpoint accepts at most ${BANK_ACCOUNT_MAX_LENGTH}).`
    : null;
}

/** The confirmation must equal the account number exactly; nothing to confirm when no account is entered. */
export function confirmAccountError(
  account: string | null | undefined,
  confirm: string | null | undefined
): string | null {
  if (!account) {
    return null;
  }
  if (!confirm) {
    return 'Enter the account number again to confirm it.';
  }
  return account === confirm ? null : 'The account numbers do not match.';
}

/** Bank name, city and state: optional; letters, digits, spaces and , . - & ' only; at most 100. */
export function bankTextError(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  if (value.length > BANK_TEXT_MAX_LENGTH) {
    return `Bank name, city and state must be at most ${BANK_TEXT_MAX_LENGTH} characters.`;
  }
  return /^[A-Za-z0-9 ,.&'-]+$/.test(value)
    ? null
    : "Bank name, city and state may contain only letters, digits, spaces and , . - & '";
}

/** Optional; at most 16 characters. */
export function comDataPayCardError(
  value: string | null | undefined
): string | null {
  return (value?.length ?? 0) > COMDATA_PAY_CARD_MAX_LENGTH
    ? `ComData pay card must be at most ${COMDATA_PAY_CARD_MAX_LENGTH} characters.`
    : null;
}

/** Optional; at most 20 characters. */
export function perDiemPayCardError(
  value: string | null | undefined
): string | null {
  return (value?.length ?? 0) > PER_DIEM_PAY_CARD_MAX_LENGTH
    ? `Per diem pay card must be at most ${PER_DIEM_PAY_CARD_MAX_LENGTH} characters.`
    : null;
}
